import React, { useState, useEffect } from 'react';
import helpdeskTicketsService, { HelpdeskTicketConfig, HelpdeskRequestType } from '../services/helpdeskTickets.service';
import { useToast } from '../contexts/ToastContext';

interface HelpdeskTicketFormProps {
  onSuccess?: (workItemId: number) => void;
  onCancel?: () => void;
  isOpen?: boolean;
  onTemporaryClose?: () => void;
  onTemporaryReopen?: () => void;
  onShowScreenshotModal?: (screenshot: string, updateCallback: (newScreenshot: string) => void) => void;
  /** Override the page_url field (used by popout windows that need to report the opener's URL) */
  initialPageUrl?: string;
}

const HelpdeskTicketForm: React.FC<HelpdeskTicketFormProps> = ({
  onSuccess,
  onCancel,
  isOpen,
  onTemporaryClose,
  onTemporaryReopen,
  onShowScreenshotModal,
  initialPageUrl,
}) => {
  const { showToast } = useToast();
  const [config, setConfig] = useState<HelpdeskTicketConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);

  const resolvedPageUrl = initialPageUrl || window.location.href;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    request_type: 'incident' as HelpdeskRequestType,
    page_url: resolvedPageUrl,
  });

  // Capture system and browser information
  const getSystemInfo = () => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const viewport = `${window.innerWidth}x${window.innerHeight}`;

    return {
      userAgent,
      platform,
      language,
      screenResolution,
      viewport,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  };

  // Capture screenshot of the current page
  const captureScreenshot = async () => {
    setCapturingScreenshot(true);
    try {
      if (onTemporaryClose) {
        onTemporaryClose();
      }
      await new Promise(resolve => setTimeout(resolve, 150));

      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await new Promise<void>(resolve => { video.onloadedmetadata = () => resolve(); });
      await video.play();
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      stream.getTracks().forEach(track => track.stop());

      setScreenshot(canvas.toDataURL('image/png'));

      if (onTemporaryReopen) {
        onTemporaryReopen();
      }
    } catch (error: any) {
      console.error('Failed to capture screenshot:', error);
      if (error?.name !== 'NotAllowedError') {
        setError('Failed to capture screenshot. Please try again.');
      }
      if (onTemporaryReopen) {
        onTemporaryReopen();
      }
    } finally {
      setCapturingScreenshot(false);
    }
  };

  // Remove screenshot
  const removeScreenshot = () => {
    setScreenshot(null);
  };

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    Array.from(files).forEach(file => {
      if (!validTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Only PNG, JPG, JPEG, and GIF are allowed.`);
        return;
      }

      if (file.size > maxSize) {
        setError(`File too large: ${file.name}. Maximum size is 10MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setUploadedImages(prev => [...prev, event.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = '';
  };

  // Remove uploaded image
  const removeUploadedImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Update uploaded image (after annotation)
  const updateUploadedImage = (index: number, newImage: string) => {
    setUploadedImages(prev => prev.map((img, i) => i === index ? newImage : img));
  };

  // Handle clipboard paste
  const handlePaste = (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (!file) continue;

        if (!validTypes.includes(file.type)) {
          setError(`Invalid file type. Only PNG, JPG, JPEG, and GIF are allowed.`);
          return;
        }

        if (file.size > maxSize) {
          setError(`File too large. Maximum size is 10MB.`);
          return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            setUploadedImages(prev => [...prev, event.target!.result as string]);
            showToast({
              message: 'Image pasted successfully!',
              type: 'success',
              duration: 2000
            });
          }
        };
        reader.readAsDataURL(file);

        e.preventDefault();
        break; // Only process the first image
      }
    }
  };

  // Fetch configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const cfg = await helpdeskTicketsService.getConfig();
        setConfig(cfg);
      } catch (err) {
        console.error('Failed to load helpdesk ticket config:', err);
      }
    };
    loadConfig();

    const currentUrl = initialPageUrl || window.location.href;
    setFormData(prev => ({
      ...prev,
      page_url: currentUrl
    }));
  }, [initialPageUrl]);

  // Update page URL when drawer opens
  useEffect(() => {
    if (isOpen) {
      const currentUrl = initialPageUrl || window.location.href;
      setFormData(prev => ({
        ...prev,
        page_url: currentUrl
      }));
    }
  }, [isOpen, initialPageUrl]);

  // Add paste event listener when form is open
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('paste', handlePaste);
      return () => {
        document.removeEventListener('paste', handlePaste);
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const systemInfo = getSystemInfo();

      const response = await helpdeskTicketsService.submitHelpdeskTicket({
        title: formData.title,
        description: formData.description,
        request_type: formData.request_type,
        page_url: formData.page_url || undefined,
        screenshot_url: screenshot || undefined,
        uploaded_images: uploadedImages.length > 0 ? uploadedImages : undefined,
        additional_info: {
          system: systemInfo
        }
      });

      showToast({
        message: `Ticket #${response.id} submitted successfully!`,
        type: 'success',
        duration: 5000
      });

      // Reset form
      setFormData({
        title: '',
        description: '',
        request_type: 'incident',
        page_url: initialPageUrl || window.location.href,
      });
      setScreenshot(null);
      setUploadedImages([]);

      if (onSuccess) {
        onSuccess(response.id);
      }

      if (onCancel) {
        onCancel();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit helpdesk ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          Helpdesk tickets are not currently configured. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-medium text-heading mb-4">
          Helpdesk Ticket
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Computer, account, software or equipment trouble? This goes to the IT Support helpdesk, not the application team.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-400">
                {error}
              </h3>
            </div>
          </div>
        </div>
      )}

      <div>
        <label htmlFor="request_type" className="block text-sm font-medium text-label">
          Request Type <span className="text-red-500">*</span>
        </label>
        <select
          id="request_type"
          name="request_type"
          required
          value={formData.request_type}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
        >
          {config.request_types.map(rt => (
            <option key={rt.value} value={rt.value}>{rt.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="title" className="block text-sm font-medium text-label">
          Subject <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          required
          minLength={5}
          maxLength={255}
          value={formData.title}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          placeholder="Brief summary of the problem or request"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-label">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          required
          minLength={10}
          rows={4}
          value={formData.description}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          placeholder="Tell us about the problem you're experiencing or what you need. The more context you share, the faster we can help."
        />
      </div>

      {/* Screenshot Section */}
      <div>
        <label className="block text-sm font-medium text-label mb-2">
          Capture Screenshot (Optional)
        </label>
        {!screenshot ? (
          <button
            type="button"
            onClick={captureScreenshot}
            disabled={capturingScreenshot}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {capturingScreenshot ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Capturing...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Capture Screenshot
              </>
            )}
          </button>
        ) : (
          <div className="relative">
            <img
              src={screenshot}
              alt="Screenshot preview"
              onClick={() => onShowScreenshotModal?.(screenshot, (newScreenshot) => setScreenshot(newScreenshot))}
              className="w-full h-auto rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-90 transition-opacity"
              title="Click to view and edit"
            />
            <button
              type="button"
              onClick={removeScreenshot}
              className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Upload Images Section */}
      <div>
        <label className="block text-sm font-medium text-label mb-2">
          Upload Images (Optional)
        </label>
        <p className="text-xs text-muted mb-2">
          You can also paste images directly from your clipboard (Ctrl+V / Cmd+V)
        </p>
        <div className="space-y-3">
          <label className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Images
            <input
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg,image/gif"
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>

          {uploadedImages.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {uploadedImages.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={image}
                    alt={`Uploaded image ${index + 1}`}
                    onClick={() => onShowScreenshotModal?.(image, (newImage) => updateUploadedImage(index, newImage))}
                    className="w-full h-auto rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-90 transition-opacity"
                    title="Click to view and edit"
                  />
                  <button
                    type="button"
                    onClick={() => removeUploadedImage(index)}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="page_url" className="block text-sm font-medium text-label">
          Page URL
        </label>
        <input
          type="text"
          id="page_url"
          name="page_url"
          value={formData.page_url}
          readOnly
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm dark:bg-gray-700 dark:text-white bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
          placeholder="https://..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Submitting...' : 'Submit Helpdesk Ticket'}
        </button>
      </div>

    </form>
  );
};

export default HelpdeskTicketForm;
